import { Step as ModelStep } from "@redotech/redo-model/return-flow";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import ShippingIcon from "@redotech/redo-web/icon-old/shipping-box.svg";
import { produce } from "immer";
import * as React from "react";
import {
  StepDownstream,
  StepId,
  StepType,
  StepTypeDetailsProps,
} from "../step";

export interface OverrideGreenReturnState {
  greenReturnOverride: boolean;
  next: StepId | undefined;
  customTitle?: string;
}

export const OVERRIDE_GREEN_RETURN = (
  Details: React.ComponentType<StepTypeDetailsProps<OverrideGreenReturnState>>,
): StepType<OverrideGreenReturnState, ModelStep.OverrideGreenReturn> => ({
  Details: (props: StepTypeDetailsProps<OverrideGreenReturnState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: "Require send back",
  valid(state) {
    return state.greenReturnOverride !== undefined;
  },
  Icon: ShippingIcon,
  description(state) {
    return state.greenReturnOverride
      ? "Require customer to return item(s)"
      : "Allow customer to keep item(s)";
  },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  empty: { next: undefined, greenReturnOverride: false },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(
        `Invalid handle index ${handleIndex} for Override Green Return step`,
      );
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  layout() {
    return BlockLayout.FULL;
  },
  toModel(state, id) {
    return {
      type: ModelStep.OVERRIDE_GREEN_RETURN,
      next: state.next ? id(state.next) : undefined,
      greenReturnOverride: state.greenReturnOverride,
    };
  },
  fromModel(model, id) {
    return {
      next: model.next ? id(model.next) : undefined,
      greenReturnOverride: model.greenReturnOverride,
    };
  },
});
